//
// Value mapping for list columns ("Werte-Zuordnung", Auswahlliste B)
// -----------------------------------------------------------------
//
// A target column that is a list only takes one of its options. The old system
// says "Kunde" or "Firma" where the template wants "Unternehmen", and without
// this every such row became ERROR in the export. A mapping row of type
// 'source' can carry a table old value → option, which the conversion applies
// before it checks the value.
//
// Only for list columns (operator, 16.10.2026). The options come from the
// template and nowhere else.
//
// Shared: the server applies and checks it, the editor offers what the server
// will accept.
//

export type TValueMapEntry = { from: string; to: string }

/** The labels of a list column, trimmed and each once; null when the column is no list. */
export function selectLabels(column: { type?: string; format?: any } | null | undefined): string[] | null {
    if (column?.type !== 'select') return null
    const options: any[] = Array.isArray(column.format?.select) ? column.format.select : []
    const labels: string[] = []
    for (const option of options) {
        const label = String(option?.label ?? '').trim()
        if (!labels.includes(label)) labels.push(label)
    }
    return labels
}

/** The key a value is looked up by: values are compared trimmed, like the labels. */
const keyOf = (value: unknown) => String(value ?? '').trim()

/**
 * The value after the mapping. A value without an entry stays as it is — the
 * check that follows reports it, as before.
 */
export function applyValueMap(map: TValueMapEntry[] | null | undefined, value: unknown): unknown {
    if (!map?.length) return value
    const key = keyOf(value)
    const entry = map.find((item) => keyOf(item.from) === key)
    return entry ? entry.to : value
}

/**
 * The mapping as it may be stored, or the reason it may not.
 *
 * Entries without a target are dropped: "no mapping" is how the editor says
 * "leave the value alone". A target that is no option of the column is
 * refused — it would only move the error from the data to the mapping.
 */
export function checkValueMap(
    input: unknown,
    labels: string[] | null
): { entries: TValueMapEntry[] } | { error: 'not-a-list' | 'invalid' | 'not-an-option'; value?: string } {
    if (input == null) return { entries: [] }
    if (!Array.isArray(input)) return { error: 'invalid' }

    const entries: TValueMapEntry[] = []
    const seen = new Set<string>()
    for (const item of input) {
        if (!item || typeof item !== 'object') return { error: 'invalid' }
        const from = keyOf((item as any).from)
        const to = (item as any).to
        if (to == null) continue
        if (typeof to !== 'string') return { error: 'invalid' }
        if (seen.has(from)) continue
        seen.add(from)
        entries.push({ from, to: to.trim() })
    }

    if (!entries.length) return { entries }
    if (!labels) return { error: 'not-a-list' }
    const wrong = entries.find((entry) => !labels.includes(entry.to))
    if (wrong) return { error: 'not-an-option', value: wrong.to }
    return { entries }
}
